// Chakra imports
import {
  Box,
  Button,
  Flex, Grid, Link, Stack,
  Switch,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import React, {useEffect, useState} from "react";
import {Element} from "react-scroll";
import {getUser} from "../../../../api/userService";
import {cancelSubscription, getSubscription} from "../../../../api/paymentService";
import {HSeparator} from "../../../../components/Separator/Separator";
import config from "../../../../config.json";


const PremiumAccount = ({
                          hasSubscribed,
                          onRefresh,
                        }) => {
  const [confirmed, setConfirmed] = useState(false);
  const bgColor = useColorModeValue("#F8F9FA", "gray.800");

  const [cancelSubscriptionButtonPressed, setCancelSubscriptionButtonPressed] = useState(false);
  const [isLoadingSubscriptionData, setIsLoadingSubscriptionData] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState({
    id: undefined,
    amount: 0.00,
    canceledAt: undefined,
    startDate: undefined,
    createdAt: undefined,
    nextPaymentDate: undefined,
    description: undefined,
    interval: undefined,
  });

  const fetchSubscriptionData = async () => {
    try {
      const response = await getSubscription();
      setSubscriptionData({
        id: response.id,
        amount: response.amount.value,
        canceledAt: response.canceledAt,
        startDate: response.startDate,
        createdAt: response.createdAt,
        nextPayment: response.nextPaymentDate,
        description: response.description,
        interval: response.interval,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingSubscriptionData(false);
    }
  };

  useEffect(() => {
    if (hasSubscribed) {
      const response = fetchSubscriptionData();
    }
  }, [hasSubscribed, isLoadingSubscriptionData]);

  const handleCancelSubscription = async () => {
    setCancelSubscriptionButtonPressed(true);
    if (confirmed && subscriptionData.id !== undefined) {
      await cancelSubscription(subscriptionData.id);
      onRefresh();
    }
    setCancelSubscriptionButtonPressed(false);
  };

  const textColor = useColorModeValue("gray.700", "white");

  return (
    <Card
      w={{sm: "100%", lg: "70%"}}
      alignSelf="flex-end"
      justifySelf="flex-end"
    >
      <Element id="premium-account" name="premium-account">
        <CardHeader mb="10px">
          <Flex direction="column">
            <Text color={textColor} fontSize="lg" fontWeight="bold" mb="4px">
              Premium Account
            </Text>
            <Text color="gray.400" fontWeight="normal" fontSize="sm">
              {hasSubscribed ?
                "Met uw premium account maakt u onbeperkt gebruik van het notuleren en alle premium features." :
                "Met een premium account kunt u onbeperkt gebruik maken van alle premium features en heeft u geen limiet op het aantal uren notuleren."
              }

            </Text>
          </Flex>
        </CardHeader>
        {
          hasSubscribed && !isLoadingSubscriptionData && (
            <CardBody mb="10px">
              <Flex direction="column" w="100%">
                <HSeparator my="22px"/>
                <Grid
                  templateColumns={{
                    sm: "1fr",
                    md: "1fr 1fr",
                    lg: "1fr 1fr",
                  }}
                  gap={{sm: "24px", lg: "54px"}}
                >
                  <Flex direction="column">
                    <Flex direction="column">
                      <Text
                        color={textColor}
                        fontSize="md"
                        fontWeight="bold"
                        mb="22px"
                      >
                        Premiumgegevens
                      </Text>
                      <Box p="24px" bg={bgColor} borderRadius="12px">
                        <Flex justify="space-between" w="100%">
                          <Flex direction="column">
                            <Text
                              color="gray.400"
                              fontSize="sm"
                              fontWeight="normal"
                            >
                              Lid sinds:{" "}
                              <Text as="span" color="gray.500" fontWeight="bold">
                                {new Date(subscriptionData.createdAt).toLocaleDateString('nl-NL')}
                              </Text>
                            </Text>
                            <Text
                              color="gray.400"
                              fontSize="sm"
                              fontWeight="normal"
                            >
                              Volgende betaling:{" "}
                              <Text as="span" color="gray.500" fontWeight="bold">
                                {new Date(subscriptionData.nextPayment).toLocaleDateString('nl-NL')}
                              </Text>
                            </Text>
                            <Text
                              color="gray.400"
                              fontSize="sm"
                              fontWeight="normal"
                            >
                              Periode betalingen:{" "}
                              <Text as="span" color="gray.500" fontWeight="bold">
                                {subscriptionData.interval === "1 month" ? "Maandelijks" : "onbekend"}
                              </Text>
                            </Text>
                          </Flex>
                        </Flex>
                      </Box>
                    </Flex>
                  </Flex>
                  <Flex direction="column" gridColumn={{md: "1 / 3", lg: "auto"}}>
                    <Text
                      color={textColor}
                      fontSize="md"
                      fontWeight="bold"
                      mb="22px"
                    >
                      Prijsoverzicht
                    </Text>
                    <Stack direction="column" spacing="12px">
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" color="gray.400" fontWeight="normal">
                          Prijs:
                        </Text>
                        <Text fontSize="sm" color={textColor} fontWeight="bold">
                          {`€ ${(subscriptionData.amount / 1.21).toFixed(2)}`}
                        </Text>
                      </Flex>
                      <Flex align="center" justify="space-between">
                        <Text fontSize="sm" color="gray.400" fontWeight="normal">
                          BTW:
                        </Text>
                        <Text fontSize="sm" color={textColor} fontWeight="bold">
                          {`€ ${(subscriptionData.amount - (subscriptionData.amount / 1.21)).toFixed(2)}`}
                        </Text>
                      </Flex>
                    </Stack>
                    <Flex align="center" justify="space-between" mt="24px">
                      <Text fontSize="md" color="gray.400" fontWeight="normal">
                        Totaal:
                      </Text>
                      <Text fontSize="md" color={textColor} fontWeight="bold">
                        {`€ ${Number(subscriptionData.amount).toFixed(2)}`}
                      </Text>
                    </Flex>
                  </Flex>
                </Grid>
                <HSeparator my="22px"/>
              </Flex>
            </CardBody>
          )
        }
        <CardBody>
          <Flex
            direction={{sm: "column", md: "row"}}
            justify="space-between"
            align="start"
            w="100%"
          >
            <Flex align="center" mb={{sm: "16px", lg: null}}>
              {hasSubscribed && (
                <>
                  <Switch isChecked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} colorScheme="green"
                          me="22px"/>
                  <Flex direction="column">
                    <Text
                      fontSize="sm"
                      color={textColor}
                      mb="4px"
                      fontWeight="semibold"
                    >
                      Opzeggen
                    </Text>
                    <Text color="gray.400" fontWeight="normal" fontSize="xs">
                      Hierbij bevestig ik dat ik mijn premium abbonoment wil opzeggen.
                    </Text>
                  </Flex>
                </>
              )}
            </Flex>
            <Flex align="center">
              {hasSubscribed ?
                (
                  <Button
                    isDisabled={!confirmed || cancelSubscriptionButtonPressed}
                    isLoading={cancelSubscriptionButtonPressed}
                    variant="solid"
                    colorScheme="red"
                    w="100px"
                    h="35px"
                    fontSize="xs"
                    onClick={async () => {
                      await handleCancelSubscription();
                    }}
                  >
                    Annuleren
                  </Button>) :
                (
                  <Link href={"/admin/dashboard/subscription"}>
                    <Button
                      variant="solid"
                      colorScheme="yellow"
                      w="150px"
                      h="35px"
                      fontSize="xs"
                    >
                      UPGRADE
                    </Button>
                  </Link>
                )}
            </Flex>
          </Flex>
        </CardBody>
      </Element>
    </Card>
  );
};

export default PremiumAccount;
